// A compare's track (logic/track.ts): its base is the merge base of its
// branches, then its commits from there to the head on the main lane,
// oldest first. A compare has no force-pushes GitHub reports and no
// reviews, so no side lanes, last push or last review. Only the newest
// commits are held of a long compare (state/compare/fetch.ts
// MAX_COMPARE_COMMITS): the commits between the base and the first held
// point are not on the track, so the main lane breaks before that point
// (`breakBefore`: its parent is not the point drawn before it). No commit
// older than the merge base belongs to a compare, so `hasOlder` is false.
import type { MainPoint, Track } from './track';

/** What a compare's track is made from (state/compare/fetch.ts `LoadedCompare`). */
export interface CompareTrackInput {
  baseRef: string;
  mergeBase: string;
  head: string;
  commits: readonly { sha: string; message: string; author: string | null; committed: string | null }[];
  truncated: boolean;
}

/** The first line of a commit message. */
const headline = (message: string) => message.split('\n', 1)[0];

/**
 * The compare's track: base the merge base (named for the base branch),
 * then its commits oldest first. The head is the newest commit; when GitHub
 * listed it nowhere (its order is by date, which a rebase can make
 * disagree with the history), it is put last with its SHA alone, and a
 * compare with no commits (the head an ancestor of the base) has its head at the base.
 */
export function compareTrack(c: CompareTrackInput): Track {
  const main: MainPoint[] = c.commits
    .filter((x) => x.sha !== c.head)
    .map((x) => ({ sha: x.sha, short: x.sha.slice(0, 7), message: headline(x.message), author: x.author, at: x.committed, breakBefore: false, forcePushedBy: null, replaced: [] }));
  const head = c.commits.find((x) => x.sha === c.head);
  if (c.head !== c.mergeBase)
    main.push({
      sha: c.head,
      short: c.head.slice(0, 7),
      message: head ? headline(head.message) : null,
      author: head?.author ?? null,
      at: head?.committed ?? null,
      breakBefore: false,
      forcePushedBy: null,
      replaced: [],
    });
  // Commits left out between the base and the first held point: the lane breaks there.
  if (c.truncated && main.length) main[0] = { ...main[0], breakBefore: true };
  return { base: { sha: c.mergeBase, name: c.baseRef, replaced: [] }, main, head: c.head, unheld: [], hasOlder: false, lastPush: null, lastReview: null };
}
